import { useLocalization, type SupportedLocale } from "../lib/i18n";

type PrivacyListItem = {
  label?: string;
  text: string;
};

type PrivacySection = {
  title: string;
  paragraphs?: string[];
  list?: PrivacyListItem[];
};

type PrivacyContent = {
  title: string;
  updated: string;
  sections: PrivacySection[];
};

function isSupportUrlParagraph(paragraph: string, section: PrivacySection) {
  const paragraphs = section.paragraphs ?? [];
  return paragraphs[paragraphs.length - 1] === paragraph && paragraph.startsWith("https://");
}

const privacyContent: Partial<Record<SupportedLocale, PrivacyContent>> = {
  en: {
    title: "Privacy Policy for Sport Exchange 100",
    updated: "Last Updated: May 16, 2026",
    sections: [
      {
        title: "1. Introduction",
        paragraphs: [
          'SportEx100 Technologies Inc. ("we," "our," or "us") operates Sport Exchange 100. We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, and share your information when you use our mobile application and related services (the "App").',
        ],
      },
      {
        title: "2. Information We Collect",
        paragraphs: [
          "We collect information that you provide to us directly, as well as data automatically collected when you use the App.",
        ],
        list: [
          {
            label: "Account Information:",
            text: "When you register, we may collect your name, username, email address, and password.",
          },
          {
            label: "App Activity:",
            text: "We collect data related to your use of the App, including your trade history, pool memberships, leaderboard rankings, simulated market activity, and related ledger records needed to keep pool accounting accurate.",
          },
          {
            label: "User Content:",
            text: "If you use pool chat or upload a profile photo, we process that content to provide the feature, moderate abuse reports, and support your account.",
          },
          {
            label: "Device Information:",
            text: "We automatically collect certain information about your device, such as your IP address, operating system (e.g., iOS version), device model, and crash reports to help us troubleshoot and improve the App.",
          },
        ],
      },
      {
        title: "3. How We Use Your Information",
        paragraphs: ["We use the information we collect to:"],
        list: [
          { text: "Create and manage your account." },
          {
            text: "Facilitate the core features of the App, including managing pools, executing trades, and updating leaderboards.",
          },
          { text: "Provide customer support and respond to your inquiries." },
          { text: "Analyze usage trends to improve the App's performance and user interface." },
          { text: "Communicate with you about important updates or changes to the App." },
        ],
      },
      {
        title: "4. How We Share Your Information",
        paragraphs: [
          "We do not sell your personal information to third parties. We may share your information in the following situations:",
        ],
        list: [
          {
            label: "With Other Users:",
            text: "Your username, avatar, and performance data (like standings and points) will be visible to other users within the pools you join. Pool chat messages are visible to members of that pool.",
          },
          {
            label: "After Account Deletion:",
            text: "If you delete your account, your personal account identifiers are removed or anonymized. Anonymized pool activity, trades, positions, and transaction records may remain visible or be retained where needed to preserve pool accounting, leaderboard integrity, dispute review, and security.",
          },
          {
            label: "Service Providers:",
            text: "We may share data with third-party vendors who perform services on our behalf (e.g., cloud hosting, analytics, database management).",
          },
          {
            label: "Legal Obligations:",
            text: "We may disclose your information if required to do so by law or in response to valid requests by public authorities.",
          },
        ],
      },
      {
        title: "5. Data Security",
        paragraphs: [
          "We implement industry-standard security measures to protect your personal information from unauthorized access, use, or disclosure. However, no method of transmission over the internet or electronic storage is 100% secure, and we cannot guarantee absolute security.",
        ],
      },
      {
        title: "6. Your Privacy Rights",
        paragraphs: [
          "Depending on your location, you may have the right to access, update, or delete the personal information we hold about you. You can usually manage your account information directly within the App's settings. In the mobile app, you can initiate deletion of your account from the account sheet by opening your avatar or trader code and selecting Delete account.",
          "When an account is deleted, we remove or anonymize account identifiers, scrub personal profile fields, and disable future access. Deletion does not necessarily remove all simulated trading records. We may retain anonymized pool activity, trades, positions, and transaction records where needed to preserve pool accounting, leaderboard integrity, dispute review, and security. These retained records are not intended to identify you personally after deletion.",
          "You can contact us through support for privacy questions, including questions about deletion or retained anonymized records.",
        ],
      },
      {
        title: "7. Changes to This Privacy Policy",
        paragraphs: [
          'We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.',
        ],
      },
      {
        title: "8. Contact Us",
        paragraphs: [
          "If you have questions or comments about this policy, contact SportEx100 Technologies Inc. through the Sport Exchange 100 support form.",
          "https://sportex100.com/support",
        ],
      },
    ],
  },
  fr: {
    title: "Politique de confidentialité de Sport Exchange 100",
    updated: "Dernière mise à jour : 16 mai 2026",
    sections: [
      {
        title: "1. Introduction",
        paragraphs: [
          'SportEx100 Technologies Inc. (« nous », « notre » ou « nos ») exploite Sport Exchange 100. Nous nous engageons à protéger vos renseignements personnels et votre droit à la vie privée. La présente Politique de confidentialité explique comment nous recueillons, utilisons et partageons vos renseignements lorsque vous utilisez notre application mobile et les services connexes (l’« Application »).',
        ],
      },
      {
        title: "2. Renseignements que nous recueillons",
        paragraphs: [
          "Nous recueillons les renseignements que vous nous fournissez directement, ainsi que certaines données recueillies automatiquement lorsque vous utilisez l’Application.",
        ],
        list: [
          {
            label: "Renseignements de compte :",
            text: "Lors de votre inscription, nous pouvons recueillir votre nom, votre nom d’utilisateur, votre adresse courriel et votre mot de passe.",
          },
          {
            label: "Activité dans l’Application :",
            text: "Nous recueillons des données liées à votre utilisation de l’Application, notamment votre historique de transactions, vos adhésions à des pools, vos classements, votre activité de marché simulée et les registres connexes nécessaires pour maintenir une comptabilité exacte des pools.",
          },
          {
            label: "Contenu utilisateur :",
            text: "Si vous utilisez le clavardage d’un pool ou téléversez une photo de profil, nous traitons ce contenu afin de fournir la fonctionnalité, modérer les signalements d’abus et soutenir votre compte.",
          },
          {
            label: "Renseignements sur l’appareil :",
            text: "Nous recueillons automatiquement certains renseignements sur votre appareil, comme votre adresse IP, votre système d’exploitation (par exemple, la version iOS), le modèle de l’appareil et les rapports de plantage, afin de nous aider à dépanner et améliorer l’Application.",
          },
        ],
      },
      {
        title: "3. Utilisation de vos renseignements",
        paragraphs: ["Nous utilisons les renseignements recueillis pour :"],
        list: [
          { text: "Créer et gérer votre compte." },
          {
            text: "Fournir les fonctionnalités principales de l’Application, y compris la gestion des pools, l’exécution des transactions et la mise à jour des classements.",
          },
          { text: "Fournir du soutien à la clientèle et répondre à vos demandes." },
          { text: "Analyser les tendances d’utilisation afin d’améliorer la performance et l’interface de l’Application." },
          { text: "Vous communiquer les mises à jour ou changements importants concernant l’Application." },
        ],
      },
      {
        title: "4. Partage de vos renseignements",
        paragraphs: [
          "Nous ne vendons pas vos renseignements personnels à des tiers. Nous pouvons partager vos renseignements dans les situations suivantes :",
        ],
        list: [
          {
            label: "Avec d’autres utilisateurs :",
            text: "Votre nom d’utilisateur, votre avatar et vos données de performance (comme les classements et les points) seront visibles par les autres utilisateurs des pools auxquels vous participez. Les messages de clavardage d’un pool sont visibles par les membres de ce pool.",
          },
          {
            label: "Après la suppression du compte :",
            text: "Si vous supprimez votre compte, vos identifiants personnels sont supprimés ou anonymisés. Les activités de pool anonymisées, transactions, positions et registres de transactions peuvent demeurer visibles ou être conservés lorsque cela est nécessaire pour préserver la comptabilité des pools, l’intégrité des classements, l’examen des différends et la sécurité.",
          },
          {
            label: "Fournisseurs de services :",
            text: "Nous pouvons partager des données avec des fournisseurs tiers qui fournissent des services en notre nom (par exemple, hébergement infonuagique, analyses et gestion de base de données).",
          },
          {
            label: "Obligations légales :",
            text: "Nous pouvons divulguer vos renseignements si la loi l’exige ou en réponse à des demandes valides d’autorités publiques.",
          },
        ],
      },
      {
        title: "5. Sécurité des données",
        paragraphs: [
          "Nous mettons en œuvre des mesures de sécurité courantes dans l’industrie pour protéger vos renseignements personnels contre l’accès, l’utilisation ou la divulgation non autorisés. Toutefois, aucune méthode de transmission sur Internet ni de stockage électronique n’est sécurisée à 100 %, et nous ne pouvons garantir une sécurité absolue.",
        ],
      },
      {
        title: "6. Vos droits en matière de confidentialité",
        paragraphs: [
          "Selon votre lieu de résidence, vous pouvez avoir le droit d’accéder aux renseignements personnels que nous détenons à votre sujet, de les mettre à jour ou de les supprimer. Vous pouvez habituellement gérer les renseignements de votre compte directement dans les réglages de l’Application. Dans l’application mobile, vous pouvez demander la suppression de votre compte à partir de la feuille de compte en ouvrant votre avatar ou votre code de trader, puis en sélectionnant Supprimer le compte.",
          "Lorsqu’un compte est supprimé, nous supprimons ou anonymisons les identifiants du compte, effaçons les champs de profil personnels et désactivons l’accès futur. La suppression ne retire pas nécessairement tous les registres de transactions simulées. Nous pouvons conserver les activités de pool anonymisées, transactions, positions et registres de transactions lorsque cela est nécessaire pour préserver la comptabilité des pools, l’intégrité des classements, l’examen des différends et la sécurité. Ces registres conservés ne sont pas destinés à vous identifier personnellement après la suppression.",
          "Vous pouvez nous contacter par l’assistance pour toute question relative à la confidentialité, y compris les questions concernant la suppression ou les registres anonymisés conservés.",
        ],
      },
      {
        title: "7. Modifications de la présente Politique",
        paragraphs: [
          "Nous pouvons mettre à jour la présente Politique de confidentialité de temps à autre. Nous vous informerons des changements en publiant la nouvelle Politique de confidentialité sur cette page et en mettant à jour la date de « Dernière mise à jour ».",
        ],
      },
      {
        title: "8. Nous contacter",
        paragraphs: [
          "Si vous avez des questions ou commentaires au sujet de la présente politique, contactez SportEx100 Technologies Inc. au moyen du formulaire d’assistance de Sport Exchange 100.",
          "https://sportex100.com/support",
        ],
      },
    ],
  },
  ja: {
    title: "Sport Exchange 100 プライバシーポリシー",
    updated: "最終更新日：2026年5月16日",
    sections: [
      {
        title: "1. はじめに",
        paragraphs: [
          "SportEx100 Technologies Inc.（以下「当社」といいます）は Sport Exchange 100 を運営しています。当社は、お客様の個人情報およびプライバシーの権利を保護することに努めています。本プライバシーポリシーは、お客様が当社のモバイルアプリケーションおよび関連サービス（以下「本アプリ」といいます）を利用する際に、当社がどのように情報を収集、利用、共有するかを説明するものです。",
        ],
      },
      {
        title: "2. 収集する情報",
        paragraphs: [
          "当社は、お客様が直接提供する情報、および本アプリの利用時に自動的に収集されるデータを収集します。",
        ],
        list: [
          {
            label: "アカウント情報：",
            text: "登録時に、氏名、ユーザー名、メールアドレス、パスワードを収集する場合があります。",
          },
          {
            label: "アプリ内アクティビティ：",
            text: "取引履歴、プール参加状況、ランキング、シミュレーション市場での活動、プール会計を正確に保つために必要な関連台帳記録など、本アプリの利用に関するデータを収集します。",
          },
          {
            label: "ユーザーコンテンツ：",
            text: "プールチャットを利用したりプロフィール写真をアップロードしたりする場合、当社はその機能の提供、不正利用報告のモデレーション、アカウントサポートのために当該コンテンツを処理します。",
          },
          {
            label: "端末情報：",
            text: "IPアドレス、オペレーティングシステム（例：iOS バージョン）、端末モデル、クラッシュレポートなど、端末に関する一定の情報を自動的に収集し、本アプリのトラブルシューティングおよび改善に役立てます。",
          },
        ],
      },
      {
        title: "3. 情報の利用目的",
        paragraphs: ["当社は収集した情報を次の目的で利用します。"],
        list: [
          { text: "アカウントの作成および管理。" },
          {
            text: "プール管理、取引の実行、ランキング更新など、本アプリの主要機能の提供。",
          },
          { text: "カスタマーサポートの提供およびお問い合わせへの対応。" },
          { text: "利用傾向の分析による本アプリのパフォーマンスおよびユーザーインターフェースの改善。" },
          { text: "本アプリに関する重要な更新または変更のご連絡。" },
        ],
      },
      {
        title: "4. 情報の共有",
        paragraphs: [
          "当社は、お客様の個人情報を第三者に販売しません。次の場合に情報を共有することがあります。",
        ],
        list: [
          {
            label: "他のユーザーとの共有：",
            text: "ユーザー名、アバター、ランキングやポイントなどの成績データは、参加しているプール内の他のユーザーに表示されます。プールチャットのメッセージは、そのプールのメンバーに表示されます。",
          },
          {
            label: "アカウント削除後：",
            text: "アカウントを削除した場合、個人アカウント識別子は削除または匿名化されます。匿名化されたプール活動、取引、ポジション、取引記録は、プール会計、ランキングの完全性、紛争確認、セキュリティを維持するために必要な場合、表示または保持されることがあります。",
          },
          {
            label: "サービス提供者：",
            text: "クラウドホスティング、分析、データベース管理など、当社に代わってサービスを提供する第三者ベンダーとデータを共有することがあります。",
          },
          {
            label: "法的義務：",
            text: "法律により要求される場合、または公的機関からの有効な要請に応じる場合、情報を開示することがあります。",
          },
        ],
      },
      {
        title: "5. データセキュリティ",
        paragraphs: [
          "当社は、不正アクセス、利用、開示から個人情報を保護するため、業界標準のセキュリティ対策を実施しています。ただし、インターネット上の送信または電子的保存の方法で100%安全なものはなく、絶対的な安全性を保証することはできません。",
        ],
      },
      {
        title: "6. プライバシーに関する権利",
        paragraphs: [
          "お住まいの地域によっては、当社が保有するお客様の個人情報へのアクセス、更新、削除を求める権利を有する場合があります。通常、アカウント情報は本アプリの設定から管理できます。モバイルアプリでは、アバターまたはトレーダーコードを開き、「アカウントを削除」を選択することで、アカウント削除を開始できます。",
          "アカウントが削除されると、当社はアカウント識別子を削除または匿名化し、個人プロフィール項目を消去し、以後のアクセスを無効にします。削除により、すべてのシミュレーション取引記録が必ず削除されるとは限りません。当社は、プール会計、ランキングの完全性、紛争確認、セキュリティを維持するために必要な場合、匿名化されたプール活動、取引、ポジション、取引記録を保持することがあります。保持される記録は、削除後にお客様を個人的に識別することを意図したものではありません。",
          "削除や保持される匿名化記録に関する質問を含め、プライバシーに関する質問はサポートからお問い合わせください。",
        ],
      },
      {
        title: "7. 本ポリシーの変更",
        paragraphs: [
          "当社は、本プライバシーポリシーを随時更新することがあります。変更がある場合は、新しいプライバシーポリシーをこのページに掲載し、「最終更新日」を更新することでお知らせします。",
        ],
      },
      {
        title: "8. お問い合わせ",
        paragraphs: [
          "本ポリシーについてご質問やご意見がある場合は、Sport Exchange 100 のサポートフォームを通じて SportEx100 Technologies Inc. までお問い合わせください。",
          "https://sportex100.com/support",
        ],
      },
    ],
  },
};

function renderListItem(item: PrivacyListItem) {
  return (
    <li key={`${item.label ?? ""}${item.text}`}>
      {item.label ? <strong>{item.label}</strong> : null} {item.text}
    </li>
  );
}

export function PrivacyPage() {
  const { locale } = useLocalization();
  const content = privacyContent[locale] ?? privacyContent.en;
  if (!content) return null;

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 md:gap-6 px-4 py-4 md:px-0">
      <section className="rounded-card border border-border-subtle bg-background-card p-4 shadow-card md:p-6">
        <h1 className="text-title-sm md:text-title-md font-bold tracking-tight text-foreground">
          {content.title}
        </h1>
        <p className="mt-2 text-body-sm text-foreground-muted">{content.updated}</p>
      </section>

      <section className="rounded-card border border-border-subtle bg-background-card p-4 shadow-card md:p-6">
        <div className="space-y-5 text-body-sm text-foreground">
          {content.sections.map((section) => (
            <div key={section.title}>
              <h2 className="text-title-xs font-semibold text-foreground">{section.title}</h2>
              {section.paragraphs?.map((paragraph) => (
                <p
                  key={paragraph}
                  className={isSupportUrlParagraph(paragraph, section)
                    ? "mt-1 font-semibold text-foreground"
                    : "mt-2"}
                >
                  {paragraph}
                </p>
              ))}
              {section.list ? (
                <ul className="mt-2 list-disc space-y-1 pl-5">
                  {section.list.map(renderListItem)}
                </ul>
              ) : null}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
